import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  TextField,
  Typography,
} from "@mui/material";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import YouTubeIcon from "@mui/icons-material/YouTube";
import { Helmet } from "react-helmet";
import { doc, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";

const createGuestSuffix = () => {
  if (window.crypto?.getRandomValues) {
    const value = new Uint32Array(1);
    window.crypto.getRandomValues(value);
    return String(1000 + (value[0] % 9000));
  }
  return String(Math.floor(1000 + Math.random() * 9000));
};

const cleanNickname = (value) =>
  value
    .trim()
    .replace(/[^a-zA-ZÀ-ÿ0-9_-]/g, "")
    .slice(0, 18);

function Home() {
  const navigate = useNavigate();
  const [nickname, setNickname] = useState("");
  const [isEntering, setIsEntering] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const username = localStorage.getItem("username");

  useEffect(() => {
    if (!username) return;
    updateDoc(doc(db, "users", username), {
      lastOnline: serverTimestamp(),
    }).catch(() => {
      // Una sesión temporal anterior puede haber expirado o sido eliminada.
    });
  }, [username]);

  const enterAsGuest = async () => {
    if (isEntering) return;

    const cleanName = cleanNickname(nickname) || "Invitado";
    const temporaryUsername = `${cleanName}-${createGuestSuffix()}`;

    try {
      setIsEntering(true);
      setErrorMessage("");

      await setDoc(doc(db, "users", temporaryUsername), {
        username: temporaryUsername,
        displayName: cleanName,
        role: "normal",
        monOro: 0,
        online: true,
        isBlocked: false,
        isTemporary: true,
        firstTime: false,
        welcomeMessageSent: true,
        createdAt: serverTimestamp(),
        lastOnline: serverTimestamp(),
        avatarUrl: "https://chatsrd.com/estado-avatar.png",
      });

      localStorage.setItem("username", temporaryUsername);
      localStorage.setItem("displayName", cleanName);
      localStorage.setItem(
        "avatarUrl",
        "https://chatsrd.com/estado-avatar.png",
      );
      localStorage.setItem("temporarySession", "true");
      navigate("/chat", { replace: true });
    } catch (error) {
      console.error("Error al entrar como invitado:", error);
      setErrorMessage(
        "No pudimos abrir el chat. Revisa tu conexión e intenta nuevamente.",
      );
    } finally {
      setIsEntering(false);
    }
  };

  const handleNicknameKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      enterAsGuest();
    }
  };

  return (
    <>
      <Helmet>
        <title>Chat SRD - Comunidad de Loterías Dominicanas</title>
        <meta
          name="description"
          content="Entra a Chat SRD y conversa sobre loterías dominicanas, resultados y predicciones."
        />
      </Helmet>

      <Box
        sx={{
          minHeight: "100dvh",
          py: { xs: 3, sm: 6 },
          background:
            "radial-gradient(circle at 15% 10%, rgba(14,165,233,.18), transparent 30%), radial-gradient(circle at 90% 85%, rgba(99,102,241,.16), transparent 32%), #f4f7fb",
        }}
      >
        <Container maxWidth="sm">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box sx={{ textAlign: "center" }}>
              <Box
                component="img"
                src="https://chatsrd.com/logo.png"
                alt="Logo de Chat SRD"
                sx={{
                  width: { xs: 175, sm: 215 },
                  height: "auto",
                  borderRadius: 3,
                  filter: "drop-shadow(0 8px 18px rgba(15,23,42,.18))",
                }}
              />
            </Box>

            <Card
              sx={{
                borderRadius: 5,
                overflow: "hidden",
                border: "1px solid rgba(148,163,184,.3)",
                boxShadow: "0 18px 48px rgba(15,23,42,.13)",
              }}
            >
              <Box
                sx={{
                  px: { xs: 2.5, sm: 4 },
                  py: 3,
                  textAlign: "center",
                  color: "white",
                  background:
                    "linear-gradient(120deg, #172554 0%, #2563eb 62%, #06b6d4 125%)",
                }}
              >
                <Typography component="h1" variant="h5" fontWeight={900}>
                  Entra directamente al chat
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.75, opacity: 0.9 }}>
                  Puedes elegir un nick temporal o entrar sin escribir ninguno.
                </Typography>
              </Box>

              <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
                {errorMessage && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {errorMessage}
                  </Alert>
                )}

                <TextField
                  fullWidth
                  label="Tu nick (opcional)"
                  placeholder="Ejemplo: JugadorRD"
                  value={nickname}
                  onChange={(event) => setNickname(event.target.value)}
                  onKeyDown={handleNicknameKeyDown}
                  inputProps={{ maxLength: 18 }}
                  helperText="Si lo dejas vacío, recibirás un nombre de invitado automático."
                  disabled={isEntering}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />

                <Button
                  fullWidth
                  size="large"
                  variant="contained"
                  startIcon={
                    isEntering ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <LoginRoundedIcon />
                    )
                  }
                  onClick={enterAsGuest}
                  disabled={isEntering}
                  sx={{
                    mt: 2,
                    minHeight: 52,
                    borderRadius: 3,
                    fontWeight: 900,
                    background: "linear-gradient(135deg, #2563eb, #06b6d4)",
                  }}
                >
                  {isEntering ? "Entrando..." : "Entrar al chat ahora"}
                </Button>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", textAlign: "center", mt: 1.25 }}
                >
                  La sesión es temporal y tendrá permisos de invitado.
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    gap: 1,
                    mt: 3,
                    pt: 2.5,
                    borderTop: "1px solid #e2e8f0",
                  }}
                >
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PersonAddAltRoundedIcon />}
                    onClick={() => navigate("/signup")}
                    sx={{ borderRadius: 3, fontWeight: 800 }}
                  >
                    Crear cuenta
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<LoginRoundedIcon />}
                    onClick={() => navigate("/login")}
                    sx={{ borderRadius: 3, fontWeight: 800 }}
                  >
                    Iniciar sesión
                  </Button>
                </Box>
              </CardContent>
            </Card>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.25,
              }}
            >
              <Button
                variant="contained"
                startIcon={<YouTubeIcon />}
                onClick={() =>
                  window.open(
                    "https://www.youtube.com/watch?v=0mNO1MEcu7E",
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
                sx={{ bgcolor: "#ef4444", borderRadius: 3, fontWeight: 800 }}
              >
                Ver tutorial
              </Button>
              <Button
                variant="contained"
                startIcon={<WhatsAppIcon />}
                onClick={() =>
                  window.open(
                    "https://wa.me/18098216633?text=Hola,%20necesito%20asistencia%20con%20Chat%20SRD.",
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
                sx={{ bgcolor: "#16a34a", borderRadius: 3, fontWeight: 800 }}
              >
                WhatsApp
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
}

export default Home;
